import { DateTime } from "luxon";

import { datesCovered, inZone, longTime, shortTime } from "./dates.js";

/**
 * What a given day *is* in a multi-day event's life.
 *
 * Every surface in this app shows an event once per day it covers, and before
 * this module each of those days looked identical: five rows saying "Svetlana
 * in Denver — all day", with nothing to say whether she left this morning or
 * comes home tonight. The calendar was technically right and useless.
 *
 * So one function answers "where in the run is this day", and every screen
 * spells that answer in its own idiom. They cannot drift, because there is
 * only the one answer — and drift is the specific failure that matters here:
 * the phone and the wall are read in the same kitchen, ten seconds apart.
 *
 * The end is exclusive throughout, as it is everywhere else in the codebase.
 */

/** The fields of an occurrence a span is derived from. Anything else is noise. */
export interface Spanning {
  starts_at: string;
  ends_at: string | null;
  all_day: boolean;
}

export interface Span {
  /** How many household-local days the occurrence covers, at least one. */
  days: number;
  /** Where the day asked about sits in those, zero-based. */
  index: number;
  /** True when the occurrence covers more than one day at all. */
  spans: boolean;
  isFirst: boolean;
  isLast: boolean;
  /** It was already running when this day began. */
  fromEarlier: boolean;
  /** It is still running when this day ends. */
  intoLater: boolean;
}

/**
 * Where `date` falls in the occurrence's run.
 *
 * A date the occurrence does not cover is treated as its first day. That is
 * not a real state — the callers all ask about a day they put the event on —
 * but a screen that renders nothing is worse than one that renders the start.
 */
export function spanOn(occurrence: Spanning, date: string, zone: string): Span {
  const dates = datesCovered(occurrence.starts_at, occurrence.ends_at, zone);
  const found = dates.indexOf(date);
  const index = found === -1 ? 0 : found;
  const days = dates.length;

  return {
    days,
    index,
    spans: days > 1,
    isFirst: index === 0,
    isLast: index === days - 1,
    fromEarlier: index > 0,
    intoLater: index < days - 1,
  };
}

/** The last instant the occurrence actually covers, ends being exclusive. */
function lastMoment(occurrence: Spanning, zone: string): DateTime | null {
  if (occurrence.ends_at === null) return null;
  const end = inZone(occurrence.ends_at, zone);
  const start = inZone(occurrence.starts_at, zone);
  return end <= start ? null : end;
}

/**
 * How a clock time is spelled. The phone's week list has a narrow mono column
 * and takes "4:30pm"; the kitchen is read from ten feet and takes "4:30 pm".
 */
export type TimeVoice = "tight" | "roomy";

/**
 * The compact form, for the time column of a dense list.
 *
 * The arrow means one thing and only one thing: it keeps going past this day.
 * So the first day carries its start time and an arrow, the days in between
 * say which day of how many they are, and the last day either names the hour
 * it finishes or says so in words. Nothing here is wider than "all day →",
 * because the columns it lives in are not.
 */
export function spanTime(
  occurrence: Spanning,
  date: string,
  zone: string,
  voice: TimeVoice = "tight",
): string {
  const clock = (iso: string): string =>
    voice === "tight" ? shortTime(iso, zone) : longTime(iso, zone);

  const span = spanOn(occurrence, date, zone);
  const base = occurrence.all_day ? "all day" : clock(occurrence.starts_at);

  if (!span.spans) return base;
  if (span.isFirst) return `${base} →`;

  if (span.isLast) {
    const end = lastMoment(occurrence, zone);
    return occurrence.all_day || end === null ? "last day" : `→ ${clock(end.toISO() ?? "")}`;
  }

  return `day ${String(span.index + 1)}/${String(span.days)}`;
}

/**
 * The roomier form, for cards, day cells and the kitchen screen.
 *
 * "day 2 of 4" rather than an arrow, because these surfaces are read once and
 * from further away, and a fraction survives both. The time half says what is
 * actually happening on *this* day: the middle of an overnight flight is all
 * of that day, whatever the departure board said.
 */
export function spanMeta(occurrence: Spanning, date: string, zone: string): string {
  const span = spanOn(occurrence, date, zone);
  const end = lastMoment(occurrence, zone);

  if (!span.spans) {
    if (occurrence.all_day) return "all day";
    if (end === null) return longTime(occurrence.starts_at, zone);
    return `${inZone(occurrence.starts_at, zone).toFormat("h:mm").toLowerCase()} – ${longTime(end.toISO() ?? "", zone)}`;
  }

  const position = span.isLast
    ? "last day"
    : `day ${String(span.index + 1)} of ${String(span.days)}`;

  if (occurrence.all_day) return `all day · ${position}`;

  if (span.isFirst) return `from ${longTime(occurrence.starts_at, zone)} · ${position}`;
  if (span.isLast && end !== null) return `until ${longTime(end.toISO() ?? "", zone)} · ${position}`;

  return `all day · ${position}`;
}

/**
 * The same thing said in full, for a screen reader and for the `title` of a
 * chip too narrow to read. The visible forms are abbreviations of this one.
 */
export function spanAria(
  occurrence: Spanning & { title: string },
  date: string,
  zone: string,
): string {
  const span = spanOn(occurrence, date, zone);
  if (!span.spans) return `${occurrence.title}, ${spanMeta(occurrence, date, zone)}`;

  const last = lastMoment(occurrence, zone)?.minus({ milliseconds: 1 });
  const through = last === null || last === undefined ? "" : `, through ${last.toFormat("cccc")}`;

  return `${occurrence.title}, day ${String(span.index + 1)} of ${String(span.days)}${through}`;
}

/**
 * One occurrence's run clipped to one row of a calendar grid.
 *
 * `lane` is the row of the band stack it was given, held constant across the
 * whole week so a run draws as one straight line rather than a staircase.
 */
export interface Band<T> {
  item: T;
  /** Column index within the row, inclusive. */
  from: number;
  /** Column index within the row, inclusive. */
  to: number;
  lane: number;
  /** The run began before this row — the band is cut off at its left edge. */
  clippedStart: boolean;
  /** It continues past this row's right edge. */
  clippedEnd: boolean;
}

export interface Bands<T> {
  bands: Band<T>[];
  /** How many lanes the row needs. Zero when nothing spans it. */
  lanes: number;
}

/**
 * Lays multi-day events out across one row of a month grid.
 *
 * Drawing a separate chip per day is what every naive calendar does, and it is
 * why a week away reads as five unrelated things. A band is the honest shape:
 * one rectangle from the day it starts to the day it ends.
 *
 * Lanes are assigned greedily over the whole row rather than per cell, which
 * is the entire trick — a lane chosen per day lets Tuesday's band sit above
 * Wednesday's half of the same event, and the run becomes a staircase.
 * Longest first, so the run that matters most gets the top lane.
 */
export function bandsForRow<T extends Spanning>(
  items: T[],
  dates: string[],
  zone: string,
): Bands<T> {
  const spanning = items
    .map((item) => {
      const covered = datesCovered(item.starts_at, item.ends_at, zone);
      const first = covered[0] ?? "";
      const last = covered[covered.length - 1] ?? first;
      return { item, first, last, days: covered.length };
    })
    .filter((entry) => entry.days > 1 && entry.last >= (dates[0] ?? "") && entry.first <= (dates[dates.length - 1] ?? ""))
    // Longest run first, then earliest, so the top lane is the one that says
    // the most about the week. `id` is not available here, so the original
    // order breaks the remaining ties and keeps this stable across renders.
    .sort((a, b) => b.days - a.days || a.first.localeCompare(b.first));

  const lanes: string[] = [];
  const bands: Band<T>[] = [];

  for (const entry of spanning) {
    const from = Math.max(
      0,
      dates.findIndex((d) => d === entry.first),
    );
    const toIndex = dates.findIndex((d) => d === entry.last);
    const to = toIndex === -1 ? dates.length - 1 : toIndex;

    // The first lane whose last band finished before this one starts. Bands in
    // the same lane may touch end-to-start only if they are not the same day.
    let lane = lanes.findIndex((end) => end < entry.first);
    if (lane === -1) {
      lane = lanes.length;
      lanes.push(entry.last);
    } else {
      lanes[lane] = entry.last;
    }

    bands.push({
      item: entry.item,
      from,
      to,
      lane,
      clippedStart: entry.first < (dates[0] ?? ""),
      clippedEnd: entry.last > (dates[dates.length - 1] ?? ""),
    });
  }

  return { bands, lanes: lanes.length };
}

/** True when an occurrence covers more than the one day. */
export function isSpanning(occurrence: Spanning, zone: string): boolean {
  return datesCovered(occurrence.starts_at, occurrence.ends_at, zone).length > 1;
}

/**
 * What the editor prints under the two date rows once they disagree.
 *
 * Both dates are already on screen, so this is not a repetition — it is the
 * length, which is the thing nobody can read off two dates at a glance and the
 * thing they will get wrong. "3 days" is how long the fridge is empty.
 */
export function describeSpan(first: string, last: string, zone: string): string {
  const from = DateTime.fromISO(first, { zone });
  const to = DateTime.fromISO(last, { zone });
  if (!from.isValid || !to.isValid || to < from) return "";

  const days = Math.round(to.diff(from, "days").days) + 1;
  if (days <= 1) return "";

  return `${String(days)} days — ${from.toFormat("ccc LLL d")} through ${to.toFormat("ccc LLL d")}`;
}
